import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  toast,
} from "@mindstonehq/ui";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import React, { ChangeEvent, useState } from "react";
import { useMutation } from "@apollo/client/react";
import type { EvidencingFragment, FullResourceFragment } from "~/graphql/codegen/graphql";
import {
  DELETE_RESOURCE_MUTATION,
  FOLLOW_RESOURCE_MUTATION,
  UNDO_DELETE_RESOURCE_MUTATION,
  UPDATE_RESOURCE_IMAGE_MUTATION,
} from "./annotate-mutations";
import {
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PhotoIcon,
  ShareIcon,
  TrashIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { DialogRenameArticle } from "./dialogs/dialog-rename-article";
import { DialogReportArticle } from "./dialogs/dialog-report-article";
import { useCustomerSupport } from "~/hooks/customer-support";
import { DialogConfirm } from "./dialogs/dialog-confirm";
import metrics from "~/utils/metrics";
import useAuthCheck from "~/hooks/check-authenticated";
import { useLocation, useNavigate, useRevalidator } from "react-router";
import { DialogShareArticle } from "./dialogs/dialog-share-article";
import { AnnotatePanels } from "~/components/annotate/annotate";
import { useUpload } from "~/components/upload-manager/upload-manager";
import { FollowIcon, TagsIcon } from "../icons";
import { useConfig } from "~/hooks/webconfig";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export function BottomBarContent({
  article,
  setPanel,
  evidencing,
}: {
  setPanel: (p: AnnotatePanels) => void;
  article: FullResourceFragment;
  evidencing: EvidencingFragment;
}) {
  const { t } = useLingui();
  const support = useCustomerSupport();
  const webConfig = useConfig();
  const [deleteResourceMutation] = useMutation(DELETE_RESOURCE_MUTATION);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [undoDelete] = useMutation(UNDO_DELETE_RESOURCE_MUTATION);
  const [changeResourceImage] = useMutation(UPDATE_RESOURCE_IMAGE_MUTATION);
  const checkAuth = useAuthCheck();
  const { revalidate } = useRevalidator();
  const [followResource] = useMutation(FOLLOW_RESOURCE_MUTATION);
  const articleID = article.id;
  const uploadContext = useUpload();
  const remixLocation = useLocation();
  const navigate = useNavigate();

  const stripAnnotatePathName = (pathname: string): string => {
    const annotateIndex = pathname.indexOf("annotate");
    if (annotateIndex === -1) {
      // Handle the case where 'annotate' is not found
      // For example, return the original pathname or handle as needed
      return pathname;
    }
    return pathname.slice(0, annotateIndex);
  };
  const afterDeleteNavigation = () => {
    switch (true) {
      case remixLocation.pathname.includes("/library"):
        return remixLocation.pathname.split("/annotate")[0];
      case remixLocation.pathname.includes("/annotate"):
        return stripAnnotatePathName(remixLocation.pathname);
      default:
        return "/";
    }
  };

  const handleDeleteUndo = async () => {
    try {
      await undoDelete({
        variables: {
          id: articleID,
        },
      });
      toast(t(msg`Resource restored`));
    } catch (error) {
      toast.error(t(msg`We couldn't undo delete`), {
        description: t(msg`Please contact support for assistance`),
      });
    }
  };

  const handleDelete = () => {
    metrics.track("article_delete", {
      view: "annotate_action",
      article_id: articleID,
    });
    return deleteResourceMutation({
      variables: {
        id: articleID,
      },
    })
      .then((res) => {
        toast(t(msg`Resource deleted`), {
          action: (
            <Button variant="outline" onClick={handleDeleteUndo}>
              <Trans>Undo</Trans>
            </Button>
          ),
        });
        navigate(afterDeleteNavigation());
        return res;
      })
      .catch((error) => {
        toast.error(t(msg`Error`), {
          description: t(msg`We couldn't delete this resource. Please try again later.`),
        });
        throw error;
      });
  };

  const handleSave = () => {
    const authenticated = checkAuth(
      t(msg`You need to be logged in to save a resource`),
      `${location.href}`,
      "save_to_library",
    );
    if (!authenticated) {
      return;
    }
    followResource({
      variables: {
        ids: [article.id],
      },
    })
      .then(() => {
        toast(t(msg`Resource saved to your library`));
        revalidate();
      })
      .catch(() => {
        toast.error(t(msg`We couldn't save the resource. Please try again later.`));
      });
  };
  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    let files = target.files || [];
    const file = await uploadContext.upload(files[0], false);
    const url = `${webConfig.API}/file/${file}`;
    await changeResourceImage({
      variables: {
        id: article.id,
        url: url,
      },
    });
    revalidate();
  };
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="hidden sm:flex flex-row space-x-3 grow max-w-sm lg:max-w-md">
        <img src={article.mainImage} className="h-12 w-12 object-cover rounded-xs" />
        <div className={"hidden md:flex md:flex-col justify-center gap-1"}>
          <h6 className="line-clamp-1  [overflow-wrap:anywhere] ">{article.headline}</h6>
          <span className="line-clamp-1 text-sm text-seconday">{article.author || article.siteName}</span>
        </div>
      </div>
      <div className="grow max-w-sm md:max-w-md flex justify-end space-x-2">
        {article.following && (
          <>
            <Button
              variant="ghost"
              className="px-2 flex flex-row space-x-1"
              onClick={() => {
                setPanel(AnnotatePanels.Tags);
              }}
            >
              <TagsIcon className="h-6 w-6" />
              {!!article.tags?.length && <span>{article.tags?.length}</span>}
            </Button>
          </>
        )}

        {article.canShare && (
          <DialogShareArticle article={article}>
            <Button variant="ghost" className="px-2">
              <ShareIcon className="h-6 w-6" />
            </Button>
          </DialogShareArticle>
        )}
        {article.canDelete && (
          <DialogConfirm
            title={t(msg`Remove this resource?`)}
            description={t(msg`This action is irreversible. Are you sure you want to remove it?`)}
            onConfirm={handleDelete}
            confirmButtonText={t(msg`Remove`)}
            confirmButtonProps={{ variant: "destructive" }}
          >
            <Button variant="ghost" className="px-2">
              <TrashIcon className="h-6 w-6" />
            </Button>
          </DialogConfirm>
        )}
        {!article.following && (
          <Button variant="ghost" className="px-2" onClick={handleSave}>
            <FollowIcon className="h-6 w-6" />
          </Button>
        )}

        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="px-2">
              <EllipsisVerticalIcon className="h-6 w-6" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="w-56 mr-2" side="top" sideOffset={20}>
            <DropdownMenuLabel><Trans>Actions</Trans></DropdownMenuLabel>
            <DropdownMenuSeparator />
            {article.canEdit && (
              <DialogRenameArticle
                articleID={article.id}
                title={article.headline}
                onOpenChange={(o) => {
                  if (!o) {
                    // Force close the dropdown menu
                    setOpen(false);
                  }
                }}
              >
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  <PencilSquareIcon className="h-4 w-4 mr-2" />
                  <Trans>Rename</Trans>
                </DropdownMenuItem>
              </DialogRenameArticle>
            )}
            {article.canEdit && (
              <DropdownMenuItem
                onSelect={() => {
                  if (fileRef.current) fileRef.current.click();
                }}
              >
                <PhotoIcon className="h-4 w-4 mr-2" />
                <Trans>Change image</Trans>
              </DropdownMenuItem>
            )}
            <DialogReportArticle
              articleID={article.id}
              onOpenChange={(o) => {
                if (!o) {
                  // Force close the dropdown menu
                  setOpen(false);
                }
              }}
            >
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                <Trans>Report issue</Trans>
              </DropdownMenuItem>
            </DialogReportArticle>
            {support.available && (
              <DropdownMenuItem onSelect={() => support.open()}>
                <QuestionMarkCircleIcon className="h-4 w-4 mr-2" />
                <Trans>Help</Trans>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <input
        ref={fileRef}
        type="file"
        id="fileInput"
        className="hidden"
        accept="image/*"
        onChange={handleFileUpload}
        hidden
      />
    </>
  );
}
